import { Component, type ReactNode } from "react";

interface ChunkErrorBoundaryProps {
  children: ReactNode;
}

interface ChunkErrorBoundaryState {
  hasError: boolean;
  isRefreshing: boolean;
}

/**
 * Error boundary to catch chunk loading errors that occur when:
 * - Users have cached old index.html after deployment
 * - Old chunk files are referenced but no longer exist on server
 * - Lazy-loaded routes fail to load
 *
 * Automatically prompts users to refresh when a chunk error is detected.
 */
export class ChunkErrorBoundary extends Component<ChunkErrorBoundaryProps, ChunkErrorBoundaryState> {
  constructor(props: ChunkErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      isRefreshing: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ChunkErrorBoundaryState> | null {
    // Detect chunk loading errors with various error messages
    const isChunkError =
      error.message?.includes("Failed to fetch dynamically imported module") ||
      error.message?.includes("ChunkLoadError") ||
      error.message?.includes("Loading chunk") ||
      error.message?.includes("Failed to fetch") ||
      error.name === "ChunkLoadError";

    if (isChunkError) {
      console.warn("🔄 Chunk loading error detected:", error.message);
      return { hasError: true };
    }

    // Re-throw non-chunk errors to be handled elsewhere
    throw error;
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("ChunkErrorBoundary caught error:", {
      error,
      errorInfo,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    });
  }

  handleRefresh = () => {
    this.setState({ isRefreshing: true });

    // Clear service worker cache if available
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        for (const registration of registrations) {
          registration.unregister();
        }
      });
    }

    // Clear browser cache and reload with cache-busting timestamp
    const url = new URL(globalThis.location.href);
    url.searchParams.set("_t", Date.now().toString());
    globalThis.location.href = url.toString();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
            backgroundColor: "#f5f5f5",
            color: "#333",
            padding: "20px",
            fontFamily: "system-ui, -apple-system, sans-serif",
          }}
        >
          <div
            style={{
              maxWidth: "500px",
              textAlign: "center",
              backgroundColor: "white",
              padding: "40px",
              borderRadius: "12px",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
            }}
          >
            <div
              style={{
                fontSize: "48px",
                marginBottom: "20px",
              }}
            >
              🔄
            </div>
            <h1
              style={{
                fontSize: "24px",
                fontWeight: "600",
                marginBottom: "12px",
                color: "#1a1a1a",
              }}
            >
              Application Updated
            </h1>
            <p
              style={{
                fontSize: "16px",
                lineHeight: "1.6",
                marginBottom: "24px",
                color: "#666",
              }}
            >
              A new version of the application is available. Please refresh your browser to continue using the latest
              version.
            </p>
            <button
              onClick={this.handleRefresh}
              disabled={this.state.isRefreshing}
              style={{
                backgroundColor: this.state.isRefreshing ? "#ccc" : "#007bff",
                color: "white",
                border: "none",
                padding: "12px 32px",
                fontSize: "16px",
                fontWeight: "500",
                borderRadius: "6px",
                cursor: this.state.isRefreshing ? "not-allowed" : "pointer",
                transition: "background-color 0.2s",
                opacity: this.state.isRefreshing ? 0.7 : 1,
              }}
              onMouseEnter={(e) => {
                if (!this.state.isRefreshing) {
                  (e.target as HTMLButtonElement).style.backgroundColor = "#0056b3";
                }
              }}
              onMouseLeave={(e) => {
                if (!this.state.isRefreshing) {
                  (e.target as HTMLButtonElement).style.backgroundColor = "#007bff";
                }
              }}
            >
              {this.state.isRefreshing ? "Refreshing..." : "Refresh Application"}
            </button>
            <p
              style={{
                fontSize: "12px",
                color: "#999",
                marginTop: "24px",
              }}
            >
              This page will automatically reload with the latest version.
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
